// Moves/scales the custom `.drag-image` preview via a shared CSSOM rule instead of
// inline styles. Writing `element.style` mutates the `style` attribute on every
// `drag`/`dragover` event (~90/s), and ad-blockers (e.g. AdGuard) inject cosmetic
// selectors like `[style*=...]` / `:has(...)` that turn each such mutation into a
// whole-document style recalc. Editing a rule in an adopted stylesheet only
// invalidates elements matching `.drag-image` and touches no DOM attributes.

let rule: CSSStyleRule | null = null;
let lastX: number | null = null;
let lastY: number | null = null;
let lastVisible: boolean | null = null;

const getRule = () => {
  if (rule) return rule;
  const sheet = new CSSStyleSheet();
  // Adopted sheets cascade after document sheets, so this wins over the
  // `transform: scale(0)` from `.dragged_*_container` at equal specificity.
  sheet.replaceSync('.drag-image { top: 0; left: 0; }');
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  rule = sheet.cssRules[0] as CSSStyleRule;
  return rule;
};

export const moveDragPreview = (clientX: number, clientY: number) => {
  // Skip no-op writes: `drag` keeps firing while the pointer is still.
  if (clientX === lastX && clientY === lastY) return;
  lastX = clientX;
  lastY = clientY;
  // `translate` is separate from `transform`, so it isn't affected by the
  // scale transition and doesn't trigger layout like top/left would.
  getRule().style.setProperty(
    'translate',
    `${clientX + 10}px ${clientY + 10}px`
  );
};

export const setDragPreviewVisible = (visible: boolean) => {
  if (visible === lastVisible) return;
  lastVisible = visible;
  getRule().style.setProperty('transform', visible ? 'scale(1)' : 'scale(0)');
};

export const resetDragPreview = () => {
  lastX = null;
  lastY = null;
  lastVisible = null;
  const r = getRule();
  r.style.removeProperty('translate');
  // Fall back to the stylesheet's `scale(0)` so the next drag animates in again.
  r.style.removeProperty('transform');
};
